// store/forms/delForm/units/delFormUnitsActions.js

import {
  DelUnit,
  MiscEquip,
  RentalEquip,
} from "../../../../models/Del/DelUnit";
import { DelProjectUnit } from "../../../../models/Del/DelProjectUnit";

export const makeUnitActions = ({ getDraft, bump, setUnitsUi }) => ({
  setActiveUnitsTab(tab) {
    setUnitsUi({ activeTab: tab });
    bump();
  },

  setUnitActiveUnitsView(tabName, tabSearchUnits) {
    setUnitsUi({
      activeUnitView: { tabName, tabSearchUnits },
      allUnitSearch: tabSearchUnits === "all-units",
    });
    bump();
  },

  addNewUnit(unitType, emitPatch) {
    const draft = getDraft();
    const newUnitRowId = draft.addNewBlankUnit(unitType);
    bump();
    return newUnitRowId;
  },

  removeUnit(unitRowId, emitPatch) {
    const draft = getDraft();
    const patch = {
      patch_type: "unit",
      action: "remove",
      id: unitRowId,
    };
    emitPatch(patch);
    draft.removeUnit(unitRowId);
    bump();
  },

  setSelectedUnit(
    unitRowId,
    unitName,
    unitId,
    unitPlural,
    unitType,
    unitTypeName,
    emitPatch,
  ) {
    const draft = getDraft();

    // Prevent duplicate unitId selection across rows
    if (unitId != null) {
      const duplicate = draft.units.find(
        (u) => u.rowId !== unitRowId && u.unitId === unitId,
      );
      if (duplicate) return;
    }

    const unitItem = draft.units.find((u) => u.rowId === unitRowId);
    unitItem.updateUnitNameIdPlural(
      unitName,
      unitId,
      unitPlural,
      unitType,
      unitTypeName,
    );
    const patch = unitItem.toPatch("update", {
      unitId: unitItem.unitId,
      unitTypeName: unitItem.unitTypeName,
    });
    emitPatch(patch);
    bump();
  },

  updateUnitQuantity(
    unitRowId,
    quantity,
    sendDataToBackend = false,
    emitPatch,
  ) {
    const draft = getDraft();
    const unitItem = draft.units.find((u) => u.rowId === unitRowId);
    unitItem.updateUnitQuantity(quantity);
    if (sendDataToBackend) {
      const patch = unitItem.toPatch("update", {
        billableUnits: unitItem.billableUnits,
      });
      emitPatch(patch);
    }
    bump();
  },

  // misc units
  addNewBlankMiscUnit() {
    const draft = getDraft();
    draft.addNewBlankMiscUnit();
    bump();
  },

  removeMiscUnit(miscUnitRowId, emitPatch) {
    const draft = getDraft();
    draft.removeMiscUnit(miscUnitRowId);
    const patch = {
      patch_type: "misc_unit",
      action: "remove",
      id: miscUnitRowId,
    };
    emitPatch(patch);
    bump();
  },

  updateMiscUnitName(
    miscUnitRowId,
    unitName,
    sendDataToBackend = false,
    emitPatch,
  ) {
    const draft = getDraft();
    const miscUnitItem = draft.miscUnits.find((u) => u.rowId === miscUnitRowId);
    miscUnitItem.updateUnitName(unitName);
    if (sendDataToBackend) {
      const patch = miscUnitItem.toPatch("update", {
        unitName: miscUnitItem.unitName,
      });
      emitPatch(patch);
    }
    bump();
  },

  updateMiscUnitQuantity(
    miscUnitRowId,
    quantity,
    sendDataToBackend = false,
    emitPatch,
  ) {
    const draft = getDraft();
    const miscUnitItem = draft.miscUnits.find((u) => u.rowId === miscUnitRowId);
    miscUnitItem.updateQuantity(quantity);
    if (sendDataToBackend) {
      const patch = miscUnitItem.toPatch("update", {
        quantity: miscUnitItem.quantity,
      });
      emitPatch(patch);
    }
    bump();
  },

  updateMiscUnitQuantityType(miscUnitRowId, quantityType, emitPatch) {
    const draft = getDraft();
    const miscUnitItem = draft.miscUnits.find((u) => u.rowId === miscUnitRowId);
    miscUnitItem.updateQuantityType(quantityType);
    const patch = miscUnitItem.toPatch("update", {
      quantityType: miscUnitItem.quantityType,
    });
    emitPatch(patch);
    bump();
  },

  updateMiscUnitVendor(
    miscUnitRowId,
    vendor,
    sendDataToBackend = false,
    emitPatch,
  ) {
    const draft = getDraft();
    const miscUnitItem = draft.miscUnits.find((u) => u.rowId === miscUnitRowId);
    miscUnitItem.updateVendor(vendor);
    if (sendDataToBackend) {
      const patch = miscUnitItem.toPatch("update", {
        vendor: miscUnitItem.vendor,
      });
      emitPatch(patch);
    }
    bump();
  },

  // rentals
  addNewBlankRentalUnit() {
    const draft = getDraft();
    draft.addNewBlankRentalUnit();
    bump();
  },

  removeRentalUnit(rentalUnitRowId, emitPatch) {
    const draft = getDraft();
    draft.removeRentalUnit(rentalUnitRowId);
    const patch = {
      patch_type: "rental_unit",
      action: "remove",
      id: rentalUnitRowId,
    };
    emitPatch(patch);
    bump();
  },

  updateRentalUnitName(
    rentalUnitRowId,
    itemName,
    sendDataToBackend = false,
    emitPatch,
  ) {
    const draft = getDraft();
    const rentalUnitItem = draft.rentalUnits.find(
      (u) => u.rowId === rentalUnitRowId,
    );
    rentalUnitItem.updateUnitName(itemName);
    if (sendDataToBackend) {
      const patch = rentalUnitItem.toPatch("update", {
        itemName: rentalUnitItem.itemName,
      });
      emitPatch(patch);
    }
    bump();
  },

  updateRentalUnitQuantity(
    rentalUnitRowId,
    quantity,
    sendDataToBackend = false,
    emitPatch,
  ) {
    const draft = getDraft();
    const rentalUnitItem = draft.rentalUnits.find(
      (u) => u.rowId === rentalUnitRowId,
    );
    rentalUnitItem.updateQuantity(quantity);
    if (sendDataToBackend) {
      const patch = rentalUnitItem.toPatch("update", {
        quantity: rentalUnitItem.quantity,
      });
      emitPatch(patch);
    }
    bump();
  },

  updateRentalUnitQuantityType(rentalUnitRowId, quantityType, emitPatch) {
    const draft = getDraft();
    const rentalUnitItem = draft.rentalUnits.find(
      (u) => u.rowId === rentalUnitRowId,
    );
    rentalUnitItem.updateQuantityType(quantityType);
    const patch = rentalUnitItem.toPatch("update", {
      quantityType: rentalUnitItem.quantityType,
    });
    emitPatch(patch);
    bump();
  },

  updateRentalUnitVendor(
    rentalUnitRowId,
    vendor,
    sendDataToBackend = false,
    emitPatch,
  ) {
    const draft = getDraft();
    const rentalUnitItem = draft.rentalUnits.find(
      (u) => u.rowId === rentalUnitRowId,
    );
    rentalUnitItem.updateVendor(vendor);
    if (sendDataToBackend) {
      const patch = rentalUnitItem.toPatch("update", {
        vendor: rentalUnitItem.vendor,
      });
      emitPatch(patch);
    }
    bump();
  },

  handleBroadcast(patch, applyPatchToObject) {
    const draft = getDraft();
    if (!draft) return;

    const { id, action } = patch;

    if (action === "add") {
      const existing = draft.units.find((u) => u.rowId === id);
      if (existing) {
        applyPatchToObject(existing, patch);
      } else {
        const newUnit = new DelUnit(patch);
        draft.units.push(newUnit);
      }
      bump();
      return;
    }

    if (action === "remove") {
      const idx = draft.units.findIndex((u) => u.rowId === id);
      if (idx >= 0) {
        draft.units.splice(idx, 1);
        bump();
      }
      return;
    }

    const unit = draft.units.find((u) => u.rowId === id);
    if (unit) {
      applyPatchToObject(unit, patch);
      bump();
    } else {
      const newUnit = new DelUnit(patch);
      draft.units.push(newUnit);
      bump();
    }
  },

  handleMiscUnitBroadcast(patch, applyPatchToObject) {
    const draft = getDraft();
    if (!draft) return;

    const { id, action } = patch;

    if (action === "add") {
      const existing = draft.miscUnits.find((u) => u.rowId === id);
      if (existing) {
        applyPatchToObject(existing, patch);
      } else {
        const newUnit = new MiscEquip(patch);
        draft.miscUnits.push(newUnit);
      }
      bump();
      return;
    }

    if (action === "remove") {
      const idx = draft.miscUnits.findIndex((u) => u.rowId === id);
      if (idx >= 0) {
        draft.miscUnits.splice(idx, 1);
        bump();
      }
      return;
    }

    const unit = draft.miscUnits.find((u) => u.rowId === id);
    if (unit) {
      applyPatchToObject(unit, patch);
      bump();
    } else {
      const newUnit = new MiscEquip(patch);
      draft.miscUnits.push(newUnit);
      bump();
    }
  },

  handleRentalUnitBroadcast(patch, applyPatchToObject) {
    const draft = getDraft();
    if (!draft) return;

    const { id, action } = patch;

    if (action === "add") {
      const existing = draft.rentalUnits.find((u) => u.rowId === id);
      if (existing) {
        applyPatchToObject(existing, patch);
      } else {
        const newUnit = new RentalEquip(patch);
        draft.rentalUnits.push(newUnit);
      }
      bump();
      return;
    }

    if (action === "remove") {
      const idx = draft.rentalUnits.findIndex((u) => u.rowId === id);
      if (idx >= 0) {
        draft.rentalUnits.splice(idx, 1);
        bump();
      }
      return;
    }

    const unit = draft.rentalUnits.find((u) => u.rowId === id);
    if (unit) {
      applyPatchToObject(unit, patch);
      bump();
    } else {
      const newUnit = new RentalEquip(patch);
      draft.rentalUnits.push(newUnit);
      bump();
    }
  },

  // project-specific units
  setAvailableProjectUnits(unitsList) {
    const grouped = {};
    if (Array.isArray(unitsList) && unitsList.length > 0) {
      grouped["all"] = unitsList;
      unitsList.forEach((unit) => {
        const tableName = unit.unit_table_name || "other";
        if (!grouped[tableName]) {
          grouped[tableName] = [];
        }
        grouped[tableName].push(unit);
      });
    }
    setUnitsUi({ availableProjectUnits: grouped });
    bump();
  },

  clearAvailableProjectUnits() {
    setUnitsUi({ availableProjectUnits: {}, activeProjectUnitView: "all" });
    bump();
  },

  setActiveProjectUnitView(view) {
    setUnitsUi({ activeProjectUnitView: view });
    bump();
  },

  addNewProjectUnit(unitTableName, emitPatch) {
    const draft = getDraft();
    const newRowId = draft.addNewBlankProjectUnit(unitTableName);
    bump();
    return newRowId;
  },

  removeProjectUnit(rowId, emitPatch) {
    const draft = getDraft();
    const patch = {
      patch_type: "project_specific_unit",
      action: "remove",
      id: rowId,
    };
    emitPatch(patch);
    draft.removeProjectUnit(rowId);
    bump();
  },

  setSelectedProjectUnit(
    rowId,
    unitNumber,
    unitName,
    unitTableName,
    unitType,
    singularLabel,
    pluralLabel,
    emitPatch,
  ) {
    const draft = getDraft();

    // Prevent duplicate unitNumber selection across rows
    if (unitNumber != null) {
      const duplicate = draft.projectSpecificUnits.find(
        (u) => u.rowId !== rowId && u.unitNumber === unitNumber,
      );
      if (duplicate) return;
    }

    const unitItem = draft.projectSpecificUnits.find((u) => u.rowId === rowId);
    unitItem.updateUnitSelection(
      unitNumber,
      unitName,
      unitTableName,
      unitType,
      singularLabel,
      pluralLabel,
    );
    const patch = unitItem.toPatch("update", {
      unitNumber: unitItem.unitNumber,
      unitTableName: unitItem.unitTableName,
    });
    emitPatch(patch);
    bump();
  },

  updateProjectUnitQuantity(
    rowId,
    quantity,
    sendDataToBackend = false,
    emitPatch,
  ) {
    const draft = getDraft();
    const unitItem = draft.projectSpecificUnits.find((u) => u.rowId === rowId);
    unitItem.updateQuantity(quantity);
    if (sendDataToBackend) {
      const patch = unitItem.toPatch("update", {
        quantity: unitItem.quantity,
      });
      emitPatch(patch);
    }
    bump();
  },

  handleProjectSpecificUnitBroadcast(patch, applyPatchToObject) {
    const draft = getDraft();
    if (!draft) return;

    const { id, action } = patch;

    if (action === "add") {
      const existing = draft.projectSpecificUnits.find((u) => u.rowId === id);
      if (existing) {
        applyPatchToObject(existing, patch);
      } else {
        const newUnit = new DelProjectUnit(patch);
        draft.projectSpecificUnits.push(newUnit);
      }
      bump();
      return;
    }

    if (action === "remove") {
      const idx = draft.projectSpecificUnits.findIndex((u) => u.rowId === id);
      if (idx >= 0) {
        draft.projectSpecificUnits.splice(idx, 1);
        bump();
      }
      return;
    }

    const unit = draft.projectSpecificUnits.find((u) => u.rowId === id);
    if (unit) {
      applyPatchToObject(unit, patch);
      bump();
    } else {
      const newUnit = new DelProjectUnit(patch);
      draft.projectSpecificUnits.push(newUnit);
      bump();
    }
  },
});
